import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { downloadBase64File } from '../../utils/downloadBase64File';
import {
  FileText,
  Edit,
  Download,
  Eye,
  RotateCcw
} from 'lucide-react';
import { XCircle } from 'lucide-react';
import { GenericListView } from '../common/ListView';
import type { GenericListColumn, GenericListAction } from '../common/ListView/GenericListView';
import FormPageLayout from '../common/FormPageLayout';
import type { ActionButtonConfig } from '../common/FormPageLayout';
import { DocStatusPill, getDocStatusConfig, getDocStatusPillClassName, DisciplinePill } from '../common';
import CCPPARequestForm from './forms/CCPPARequestForm';
import HomeHealthPOCForm from './forms/HomeHealthPOCForm';
import NursingAddendumForm from './forms/NursingAddendumForm';
import CCPPAReauthForm, { getReauthBlockingIssues, reauthFieldErrorsFromError } from './forms/CCPPAReauthForm';
import type { ReauthPhysicianErrors } from './forms/CCPPAReauthForm';
import TXTherapiesPAForm from './forms/TXTherapiesPAForm';
import CustomForm from './forms/CustomForm';
import { useHasAnyPermission } from '../../hooks/usePermission';
import type { PermissionString } from '../../types/permissions';
import { fetchMyPatients } from '../../services/admin/patient.service';
import { FormService } from '../../services/patientmr/forms.service';
import type { FormRecordDto, FormType, FormStatus } from '../../dtos/patientmr/forms.dto';
import Banner from '../common/Banner';
import Dialog from '../common/Dialog';
import type { TherapyDiscipline } from '../../dtos/patientmr/assessments/therapy-tests.dto';
import { getAgencyProfileMissing } from '../../services/admin/agency.service';
import { OPEN_ADMIN_TAB_EVENT } from '../../constants/navigationEvents';
import { therapyDisciplines } from '../../constants/disciplines';


interface FormsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
}

// Form types for the dropdown
const FORM_TYPES: { label: string; value: FormType }[] = [
  { label: 'CCP PA Request', value: 'CCP_PA_REQUEST' },
  { label: 'Home Health POC', value: 'HOME_HEALTH_POC' },
  { label: 'Nursing Addendum', value: 'NURSING_ADDENDUM' },
  { label: 'CCP PA Reauthorization', value: 'CCP_PA_REAUTH' },
  { label: 'TX Medicaid Therapies PA (F00139)', value: 'TX_THERAPIES_PA' },
  { label: 'Custom', value: 'CUSTOM' }
];

const Forms: React.FC<FormsProps> = ({
  expanded,
  toggleExpanded,
  patientId
}) => {
  const canCreateForm = useHasAnyPermission([
    'patientmr:phi:form:create' as any,
    'patientmr:phi:form:createall' as any
  ]);

  const canReadForm = useHasAnyPermission([
    'patientmr:phi:form:read' as any,
    'patientmr:phi:form:readall' as any
  ]);

  const hasUpdate = useHasAnyPermission([
    'patientmr:phi:form:update' as any
  ]);

  const hasUpdateAll = useHasAnyPermission([
    'patientmr:phi:form:updateall' as any
  ]);

  // Phase 12 (§4.4): Approve / Reject are QA decisions (same gate as Start QA elsewhere)
  const canQAForm = useHasAnyPermission([
    'patientmr:qa' as PermissionString,
    'patientmr:phi:supervisor:finalize' as PermissionString,
    'patientmr:phi:supervisor:approve' as PermissionString
  ]);

  // Aggregate for existing checks (read-only computation etc.)
  const canUpdateForm = hasUpdate || hasUpdateAll;

  // Read-only mode: has read but no write permissions
  const isReadOnlyView = canReadForm && !canCreateForm && !canUpdateForm;

  const [forms, setForms] = useState<FormRecordDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Phase 19 F10/F14: "Fill from chart"; the form remounts with the filled data
  const [filling, setFilling] = useState(false);
  const [formRevision, setFormRevision] = useState(0);
  const [fillNotice, setFillNotice] = useState<string | null>(null);
  // Phase 19 F23: F00139 asks which therapy discipline's chart to fill from
  const [disciplinePickerOpen, setDisciplinePickerOpen] = useState(false);
  const [reauthFieldErrors, setReauthFieldErrors] = useState<ReauthPhysicianErrors | undefined>(undefined);
  // 422 AGENCY_PROFILE_INCOMPLETE from PDF generation: the agency fields to fill in
  const [pdfAgencyMissing, setPdfAgencyMissing] = useState<string[] | null>(null);
  const [assignedPatientIds, setAssignedPatientIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const loadAssigned = async () => {
      if (!hasUpdate) {
        setAssignedPatientIds(new Set());
        return;
      }
      try {
        const patients = await fetchMyPatients();
        if (cancelled) return;
        const ids = new Set(patients.map(p => p.id?.toString?.()));
        setAssignedPatientIds(ids);
      } catch (err) {
        console.error('Error loading assigned patients:', err);
      }
    };
    loadAssigned();
    return () => { cancelled = true; };
  }, [hasUpdate]);

  // View state: 'list' shows GenericListView, 'form' shows FormPageLayout
  const [activeView, setActiveView] = useState<'list' | 'form'>('list');
  const [selectedFormType, setSelectedFormType] = useState<FormType | null>(null);
  const [editingForm, setEditingForm] = useState<FormRecordDto | null>(null);
  const [currentFormData, setCurrentFormData] = useState<Record<string, unknown>>({});
  const [isViewOnly, setIsViewOnly] = useState(false);


  // Format date from ISO string or YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Get display name for form type
  const getFormDisplayName = (formType: FormType): string => {
    switch (formType) {
      case 'CCP_PA_REQUEST': return 'CCP PA Request';
      case 'HOME_HEALTH_POC': return 'Home Health POC';
      case 'NURSING_ADDENDUM': return 'Nursing Addendum';
      case 'CCP_PA_REAUTH': return 'CCP PA Reauthorization';
      case 'TX_THERAPIES_PA': return 'TX Medicaid Therapies PA (F00139)';
      case 'CUSTOM': return 'Custom';
      default: return formType;
    }
  };

  const getFormTypePillClassName = (): string => {
    return 'bg-slate-50 text-slate-700 border border-slate-200 px-3 py-1 rounded text-xs font-semibold';
  };

  // Define columns for the list view
  const columns = useMemo<GenericListColumn<FormRecordDto>[]>(() => [
    {
      key: 'formType',
      label: 'Type',
      width: 'w-40',
      render: (form) => (
        <span className={getFormTypePillClassName()}>{getFormDisplayName(form.formType)}</span>
      )
    },
    {
      key: 'createdAt',
      label: 'Date Created',
      width: 'w-32',
      render: (form) => formatDate(form.createdAt)
    },
    {
      key: 'updatedAt',
      label: 'Last Updated',
      width: 'w-32',
      render: (form) => formatDate(form.updatedAt)
    },
    {
      key: 'status',
      label: 'Status',
      width: 'w-24',
      align: 'center',
      render: (form) => (
        <DocStatusPill status={form.status} />
      )
    }
  ], []);

  // Define actions for each row
  const actions = useMemo<GenericListAction<FormRecordDto>[]>(() => [
    {
      icon: <Eye size={14} />,
      label: 'View',
      onClick: (form) => handleViewForm(form)
    },
    ...(canUpdateForm ? [
      {
        icon: <Edit size={14} />,
        label: 'Edit',
        onClick: (form) => handleEditForm(form),
        isVisible: (form: FormRecordDto) => {
          // Visible if user has update-all permission for any patient
          if (hasUpdateAll) return true;
          // If user has the per-patient update permission, ensure the patient is assigned to them
          if (hasUpdate) {
            const pid = form.patientId?.toString?.();
            if (pid && assignedPatientIds.has(pid)) return true;
          }
          // Otherwise, not visible
          return false;
        }
      },
      {
        icon: <RotateCcw size={14} />,
        label: 'Reopen',
        onClick: (form) => handleReopenFormRecord(form),
        isVisible: (form: FormRecordDto) => form.status === 'COMPLETE',
        disabled: reopening
      }
      ,
      {
        icon: <XCircle size={14} />,
        label: 'Expire',
        onClick: (form) => handleExpireFormRecord(form),
        isVisible: (form: FormRecordDto) => form.status === 'CURRENT' && (hasUpdateAll || (hasUpdate && form.patientId && assignedPatientIds.has(form.patientId.toString()))),
        disabled: expiring
      }
    ] : []),
    {
      icon: <Download size={14} />,
      label: 'Download',
      onClick: (form) => handleDownloadForm(form)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- row-action handlers are recreated every render; listing them would defeat the memo
  ], [canUpdateForm, hasUpdate, hasUpdateAll, assignedPatientIds, reopening, expiring]);

  // Mobile card configuration
  const mobileCardConfig = useMemo(() => ({
    primaryText: (form: FormRecordDto) => getFormDisplayName(form.formType),
    secondaryText: (form: FormRecordDto) => `Created: ${formatDate(form.createdAt)}`,
    tertiaryText: (form: FormRecordDto) => `Updated: ${formatDate(form.updatedAt)}`,
    badges: (form: FormRecordDto) => [
      {
        label: getFormDisplayName(form.formType),
        className: getFormTypePillClassName()
      },
      {
        label: getDocStatusConfig(form.status).label,
        className: getDocStatusPillClassName(form.status)
      }
    ]
  }), []);

  // Load forms from API
  const fetchForms = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await FormService.list(patientId);
      setForms(result);
    } catch (err: any) {
      console.error('Error loading forms:', err);
      setError(err.message || 'Failed to load forms. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      fetchForms();
    }
  }, [patientId, fetchForms]);

  // Handle form actions
  // Shared async loader to fetch full form data from API
  const loadFormForView = useCallback(async (form: FormRecordDto, viewOnly: boolean) => {
    if (!patientId || !form.formId) {
      setError('Invalid patient or form ID');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const fullForm = await FormService.get(patientId, form.formId);
      setEditingForm(fullForm);
      setSelectedFormType(fullForm.formType);
      setIsViewOnly(viewOnly);
      setActiveView('form');
    } catch (err: any) {
      console.error(`Error loading form ${form.formId}:`, err);
      setError(err.message || 'Failed to load form. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const handleViewForm = (form: FormRecordDto) => {
    loadFormForView(form, true);
  };

  const handleEditForm = (form: FormRecordDto) => {
    loadFormForView(form, false);
  };

  // Phase 19 F7: Download in the list (was a stub). Custom forms open their attached file; every
  // other type generates the PDF (the server keeps each generated version).
  const handleDownloadForm = async (form: FormRecordDto) => {
    if (!patientId) return;
    setError(null);
    try {
      if (form.formType === 'CUSTOM') {
        const doc = form.documents?.[0];
        if (!doc) {
          setError('This custom form has no file attached.');
          return;
        }
        const { url } = await FormService.getDocumentUrl(patientId, form.formId, doc.id);
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      const result = await FormService.generatePdf(patientId, form.formId);
      downloadBase64File(result.pdf, result.contentType, result.filename);
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Download failed. Please try again.');
    }
  };

  const handleCreateForm = useCallback((formType: FormType) => {
    setEditingForm(null);
    setSelectedFormType(formType);
    setIsViewOnly(false);
    setActiveView('form');
  }, []);

  useEffect(() => {
    if (activeView !== 'form' || !selectedFormType) return;
    setCurrentFormData((editingForm?.data as Record<string, unknown>) ?? {});
    setReauthFieldErrors(undefined);
  }, [activeView, selectedFormType, editingForm]);

  const handleFormDataChange = useCallback((data: any) => {
    setCurrentFormData((data as Record<string, unknown>) ?? {});
  }, []);

  const handleBack = () => {
    setActiveView('list');
    setSelectedFormType(null);
    setEditingForm(null);
    setIsViewOnly(false);
    fetchForms();
  };

  const handleSaveDraft = async () => {
    if (!patientId || !selectedFormType) return;
    try {
      setSaving(true);
      setError(null);
      if (editingForm) {
        const updated = await FormService.update(patientId, editingForm.formId, { data: currentFormData });
        setEditingForm(updated);
      } else {
        const created = await FormService.create(patientId, { formType: selectedFormType, data: currentFormData });
        setEditingForm(created);
      }
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.message || 'Failed to save form. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /** F00070: criteria + physician name/NPI must be complete before Submit / Generate PDF. */
  const blockReauth = (setMessage: (message: string) => void): boolean => {
    if (selectedFormType !== 'CCP_PA_REAUTH') return false;
    const issues = getReauthBlockingIssues(currentFormData);
    setReauthFieldErrors(issues?.fieldErrors);
    if (!issues) return false;
    setMessage(issues.message);
    return true;
  };

  const handleSubmitForm = async () => {
    if (!patientId || !editingForm) return;
    setError(null);
    if (blockReauth(setError)) return;
    try {
      setSubmitting(true);
      const updated = await FormService.submit(patientId, editingForm.formId);
      setEditingForm(updated);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      const reauthErrors = reauthFieldErrorsFromError(err);
      if (reauthErrors) setReauthFieldErrors(reauthErrors);
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalizeForm = async () => {
    if (!patientId || !editingForm) return;
    try {
      setSubmitting(true);
      setError(null);
      const updated = await FormService.finalize(patientId, editingForm.formId);
      setEditingForm(updated);
    } catch (err: any) {
      console.error('Error finalizing form:', err);
      setError(err.message || 'Failed to finalize form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopenFormRecord = async (form: FormRecordDto) => {
    if (!patientId || !form?.formId) return;
    try {
      setReopening(true);
      setError(null);
      const updated = await FormService.reopen(patientId, form.formId, { reason: 'Returned for revision' });
      if (editingForm?.formId === updated.formId) {
        setEditingForm(updated);
      }
      setForms(prev => prev.map(item => (item.formId === updated.formId ? updated : item)));
    } catch (err: any) {
      console.error('Error reopening form:', err);
      setError(err.message || 'Failed to reopen form. Please try again.');
    } finally {
      setReopening(false);
    }
  };

  const handleExpireFormRecord = async (form: FormRecordDto) => {
    if (!patientId || !form?.formId) return;
    try {
      setExpiring(true);
      setError(null);
      const updated = await FormService.expire(patientId, form.formId);
      if (editingForm?.formId === updated.formId) {
        setEditingForm(updated);
      }
      setForms(prev => prev.map(item => (item.formId === updated.formId ? updated : item)));
    } catch (err: any) {
      console.error('Error expiring form:', err);
      setError(err.message || 'Failed to expire form. Please try again.');
    } finally {
      setExpiring(false);
    }
  };

  const handleReopenForm = async () => {
    if (!editingForm) return;
    await handleReopenFormRecord(editingForm);
  };

  const handleApprovalReceived = async () => {
    if (!patientId || !editingForm) return;
    try {
      setSubmitting(true);
      setError(null);
      const updated = await FormService.approvalReceived(patientId, editingForm.formId);
      setEditingForm(updated);
    } catch (err: any) {
      console.error('Error recording approval:', err);
      setError(err.message || 'Failed to record approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const runFillFromChart = async (discipline?: TherapyDiscipline) => {
    if (!patientId || !editingForm) return;
    try {
      setFilling(true);
      setFillNotice(null);
      const updated = await FormService.fillFromChart(patientId, editingForm.formId, currentFormData, false, discipline);
      const filled = Object.keys(updated.prefillSources || {}).length;
      const missing = (updated.prefillMissing || []).map(m => m.label);
      setEditingForm(updated);
      setCurrentFormData((updated.data as Record<string, unknown>) ?? {});
      setFormRevision(r => r + 1);
      setFillNotice(`${filled} field${filled === 1 ? '' : 's'} filled from the chart${discipline ? ` (${discipline})` : ''}. Fields you had already typed were kept.`
        + (missing.length ? ` Still missing in the record: ${missing.join('; ')}.` : ''));
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Could not fill the form from the chart.');
    } finally {
      setFilling(false);
    }
  };

  const buildFormActions = (): ActionButtonConfig[] => {
    const currentStatus: FormStatus = editingForm?.status ?? 'DRAFT';
    const anyBusy = saving || submitting || reopening;
    const isReadOnly = isViewOnly || isReadOnlyView;
    const canShowGenerate = (
      selectedFormType === 'HOME_HEALTH_POC' ||
      selectedFormType === 'NURSING_ADDENDUM' ||
      selectedFormType === 'CCP_PA_REQUEST' ||
      selectedFormType === 'CCP_PA_REAUTH' ||
      selectedFormType === 'TX_THERAPIES_PA'
    );

    const canFill = selectedFormType === 'HOME_HEALTH_POC' || selectedFormType === 'CCP_PA_REQUEST'
      || selectedFormType === 'NURSING_ADDENDUM' || selectedFormType === 'TX_THERAPIES_PA';

    return [
      {
        key: 'fillFromChart',
        label: 'Fill from chart',
        loadingLabel: 'Filling...',
        onClick: async () => {
          if (selectedFormType === 'TX_THERAPIES_PA') {
            setDisciplinePickerOpen(true);
            return;
          }
          await runFillFromChart();
        },
        variant: 'secondary' as const,
        loading: filling,
        disabled: anyBusy || filling,
        hidden: isReadOnly || !canFill || !editingForm || currentStatus !== 'DRAFT',
      },
      {
        key: 'generatePdf',
        label: 'Generate PDF',
        onClick: async () => {
          if (!patientId || !editingForm) return;
          setPdfError(null);
          setPdfAgencyMissing(null);
          if (blockReauth(setPdfError)) return;
          try {
            setPdfLoading(true);
            const result = await FormService.generatePdf(patientId, editingForm.formId);
            downloadBase64File(result.pdf, result.contentType, result.filename);
          } catch (err: any) {
            const missing = getAgencyProfileMissing(err);
            const reauthErrors = reauthFieldErrorsFromError(err);
            if (reauthErrors) setReauthFieldErrors(reauthErrors);
            if (missing) {
              setPdfAgencyMissing(missing);
            } else {
              console.error('PDF generation failed:', err instanceof Error ? err.message : 'unknown error');
              setPdfError(err?.message || 'PDF generation failed. Please try again.');
            }
          } finally {
            setPdfLoading(false);
          }
        },
        variant: 'primary',
        loading: pdfLoading,
        disabled: pdfLoading || !editingForm,
        hidden: !canShowGenerate,
      },
      {
        key: 'close',
        label: 'Close',
        onClick: handleBack,
        variant: 'secondary',
        disabled: anyBusy,
      },
      {
        key: 'save',
        label: 'Save',
        loadingLabel: 'Saving...',
        onClick: handleSaveDraft,
        variant: 'secondary',
        loading: saving,
        disabled: anyBusy,
        hidden: isReadOnly || currentStatus !== 'DRAFT',
      },
      {
        key: 'submit',
        label: 'Submit',
        loadingLabel: 'Submitting...',
        onClick: handleSubmitForm,
        variant: 'primary',
        loading: submitting,
        disabled: anyBusy || !editingForm,
        hidden: isReadOnly || currentStatus !== 'DRAFT',
      },
      {
        key: 'reject',
        label: 'Reject',
        loadingLabel: 'Rejecting...',
        onClick: handleReopenForm,
        variant: 'danger',
        loading: reopening,
        disabled: anyBusy,
        hidden: isReadOnly || !canQAForm || (currentStatus !== 'SUBMITTED' && currentStatus !== 'QA'),
      },
      {
        key: 'approve',
        label: 'Approve',
        loadingLabel: 'Approving...',
        onClick: handleFinalizeForm,
        variant: 'success',
        loading: submitting,
        disabled: anyBusy,
        hidden: isReadOnly || !canQAForm || (currentStatus !== 'SUBMITTED' && currentStatus !== 'QA'),
      },
      {
        key: 'reopen',
        label: 'Reopen',
        loadingLabel: 'Reopening...',
        onClick: handleReopenForm,
        variant: 'primary',
        loading: reopening,
        disabled: anyBusy,
        hidden: isReadOnly || currentStatus !== 'COMPLETE',
      },
      {
        key: 'approvalReceived',
        label: 'Approval Received',
        loadingLabel: 'Recording...',
        onClick: handleApprovalReceived,
        variant: 'success',
        loading: submitting,
        disabled: anyBusy,
        hidden: isReadOnly || currentStatus !== 'COMPLETE',
      },
      {
        key: 'expire',
        label: 'Expire',
        loadingLabel: 'Expiring...',
        onClick: async () => {
          if (!patientId || !editingForm) return;
          await handleExpireFormRecord(editingForm);
        },
        variant: 'danger',
        loading: expiring,
        disabled: anyBusy,
        hidden: isReadOnly || currentStatus !== 'CURRENT' || !(hasUpdateAll || (hasUpdate && editingForm?.patientId && assignedPatientIds.has(editingForm.patientId.toString()))),
      },
    ];
  };

  const getFormTitle = (formType: FormType | null): string => {
    switch (formType) {
      case 'CCP_PA_REQUEST':
        return 'CCP Prior Authorization Request';
      case 'HOME_HEALTH_POC':
        return 'Home Health Plan of Care';
      case 'NURSING_ADDENDUM':
        return 'Nursing Addendum to Plan of Care';
      case 'CCP_PA_REAUTH':
        return 'CCP PA — PDN 6-Month Authorization';
      case 'TX_THERAPIES_PA':
        return 'TX Medicaid Physical, Occupational, or Speech Therapy PA';
      default:
        return formType || 'Form';
    }
  };

  const renderActiveForm = () => {
    const readOnly = isViewOnly || isReadOnlyView;
    const initialData = editingForm?.data ?? {};
    const handleFormRecordChange = (updated: FormRecordDto) => setEditingForm(updated);

    switch (selectedFormType) {
      case 'CCP_PA_REQUEST':
        return <CCPPARequestForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} />;
      case 'HOME_HEALTH_POC':
        return <HomeHealthPOCForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} />;
      case 'NURSING_ADDENDUM':
        return <NursingAddendumForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} />;
      case 'CCP_PA_REAUTH':
        return <CCPPAReauthForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} fieldErrors={reauthFieldErrors} />;
      case 'TX_THERAPIES_PA':
        return <TXTherapiesPAForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} />;
      case 'CUSTOM':
        return <CustomForm isReadOnly={readOnly} initialData={initialData} onDataChange={handleFormDataChange} patientId={patientId} formRecord={editingForm ?? undefined} onFormRecordChange={handleFormRecordChange} />;
      default:
        return (
          <div className="p-8 text-center text-gray-500">
            Unknown form type: {selectedFormType}
          </div>
        );
    }
  };

  const formTypeOptions = useMemo(() =>
    FORM_TYPES.map(ft => {
      const disciplineBadges: React.ReactNode =
        ft.value === 'TX_THERAPIES_PA'
          ? <>{therapyDisciplines().map(d => <DisciplinePill key={d} discipline={d} />)}</>
          : <DisciplinePill discipline="RN" />;
      return {
        label: ft.label,
        badges: disciplineBadges,
        onClick: () => handleCreateForm(ft.value)
      };
    }),
  [handleCreateForm]);

  const handleRefresh = async () => {
    if (!patientId) return;
    setRefreshing(true);
    try {
      const result = await FormService.list(patientId);
      setForms(result);
    } catch (err: any) {
      console.error('Error refreshing forms:', err);
      setError(err.message || 'Failed to refresh forms. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="w-full">
      {/* Form view — FormPageLayout */}
      {activeView === 'form' && (
        <FormPageLayout
          onBack={handleBack}
          title={getFormTitle(selectedFormType)}
          actions={buildFormActions()}
        >
          {pdfAgencyMissing && (
            <Banner
              variant="warning"
              title="Agency profile incomplete"
              className="m-4"
              data-testid="pdf-agency-missing"
              onDismiss={() => setPdfAgencyMissing(null)}
              action={(
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent(OPEN_ADMIN_TAB_EVENT, { detail: 'agencySetup' }))}
                  className="whitespace-nowrap text-sm font-semibold underline"
                >
                  Go to Agency Setup
                </button>
              )}
            >
              The PDF can&apos;t be generated until the agency profile is complete.
              {pdfAgencyMissing.length > 0 && <> Missing: {pdfAgencyMissing.join(', ')}.</>}
            </Banner>
          )}
          {pdfError && (
            <Banner variant="error" className="m-4" onDismiss={() => setPdfError(null)}>
              {pdfError}
            </Banner>
          )}
          {fillNotice && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900" role="status">{fillNotice}</div>
          )}
          <div key={formRevision}>{renderActiveForm()}</div>
        </FormPageLayout>
      )}

      {/* Phase 19 F23: F00139 fills from one therapy discipline's chart */}
      <Dialog open={disciplinePickerOpen} onClose={() => setDisciplinePickerOpen(false)} labelledBy="fill-discipline-title">
        <h2 id="fill-discipline-title" className="text-lg font-semibold text-gray-900">Fill from which chart?</h2>
        <p className="mt-1 text-sm text-gray-600">Choose the therapy discipline this request is for. Its care plan and evaluation fill the blank fields.</p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {(therapyDisciplines() as TherapyDiscipline[]).map(d => (
            <button
              key={d}
              type="button"
              className="min-h-[44px] rounded-md border border-gray-300 bg-white px-4 text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => {
                setDisciplinePickerOpen(false);
                void runFillFromChart(d);
              }}
            >
              {d}
            </button>
          ))}
        </div>
      </Dialog>

      {/* List view */}
      {activeView === 'list' && (
        <GenericListView
          title="Forms"
          icon={<FileText />}
          expanded={expanded}
          onToggleExpand={toggleExpanded}
          items={forms}
          keyExtractor={(form) => form.formId}
          columns={columns}
          actions={actions}
          mobileCard={mobileCardConfig}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          loading={loading}
          error={error}
          onDismissError={() => setError(null)}
          emptyStateIcon={<FileText />}
          emptyStateTitle="No forms available"
          emptyStateMessage="Start by creating a new form."
          showNew={!isReadOnlyView}
          newButtonLabel="Add Form"
          newButtonOptions={formTypeOptions}
        />
      )}
    </div>
  );
};

export default Forms;
